import { ActionIcon, Alert, Badge, Box, Button, Card, Group, List, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconBan, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import ErrorMessage from '../../ErrorMessage';
import SecretCreateModal from '../SecretCreateModal';
import Spinner from '../../Spinner';

// The kinds the API groups a 409's `dependents` by, with the label to show for each.
const DEPENDENT_KINDS = [
  ['webhooks', 'Webhooks'],
  ['pipelines', 'Pipelines'],
  ['connectorInstances', 'Connector instances'],
];

const SettingsTabSecrets = ({ domain }) => {
  const fetch = useFetch();

  const [secrets, setSecrets] = useState();
  const [error, setError] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState();
  const [deleteConflict, setDeleteConflict] = useState();
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState();

  const baseUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/secrets`;

  useEffect(() => {
    fetch(baseUrl)
      .then(({ secrets }) => setSecrets(secrets))
      .catch(setError);
  }, [baseUrl, fetch]);

  const handleDelete = (close) => {
    setDeleting(true);
    setDeleteError(undefined);
    setDeleteConflict(undefined);
    fetch(`${baseUrl}/${confirmDeleteId}`, { method: 'DELETE' })
      .then(() => {
        setSecrets((prev) => prev.filter((s) => s.id !== confirmDeleteId));
        close();
      })
      .catch((err) => {
        // useFetch rejects a non-2xx with the parsed body, so a 409 arrives here
        // as `{ message, dependents }`. The dependent list is too long for the
        // confirmation modal's single error line, so dismiss the modal and show
        // it against the table the operator now has to go and edit. Anything
        // else stays in the modal, normalised through getErrorMessage first —
        // the rejection is the parsed body, not an Error, and a body without a
        // top-level `message` (e.g. a 404's `{ error }`) would otherwise render
        // as a blank line, the same trap handleRevoke's catch guards against.
        if (err?.dependents) {
          setDeleteConflict(err);
          close();
        } else {
          setDeleteError(new Error(getErrorMessage(err)));
        }
      })
      .finally(() => setDeleting(false));
  };

  const handleRevoke = (close) => {
    setRevoking(true);
    setRevokeError(undefined);
    fetch(`${baseUrl}/${confirmRevokeId}/revoke`, { method: 'POST' })
      .then(({ secret }) => {
        // A 200 whose body fails to parse (or an unexpected 202/204) resolves
        // useFetch to `{}`. The POST already succeeded at that point, so still
        // close the modal and stop the spinner — only the optimistic row
        // update is skipped, since there's no `secret` to apply it from. A
        // silent no-op here (modal left open, spinner stopped, no message)
        // would read as "the revoke didn't work" when it did.
        if (!secret) {
          close();
          return;
        }
        setSecrets((prev) => prev.map((s) => (s.id === secret.id ? { ...s, ...secret } : s)));
        // The conflict alert (if showing) may reference this same secret as a
        // reason to revoke it; a successful revoke makes that advice stale.
        setDeleteConflict(undefined);
        close();
      })
      .catch((err) => {
        // ConfirmationModal renders `error.message`, and useFetch rejects with
        // the parsed body rather than an Error — normalise through
        // getErrorMessage so a 403/500/dropped-connection still shows text
        // instead of a blank modal.
        setRevokeError(new Error(getErrorMessage(err)));
      })
      .finally(() => setRevoking(false));
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!secrets) {
    return <Spinner />;
  }

  return (
    <>
      <SecretCreateModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(secret) => setSecrets((prev) => [...prev, secret])}
        baseUrl={baseUrl}
      />

      <Card withBorder>
        <Card.Section p="md">
          <Group justify="space-between" mb="md">
            <Title order={5}>Secrets</Title>
            <Button size="sm" leftSection={<IconPlus size={14} />} onClick={() => setModalOpen(true)}>
              New Secret
            </Button>
          </Group>

          {deleteConflict && (
            <Alert
              color="red"
              title={deleteConflict.message || 'Secret could not be deleted'}
              mb="md"
              withCloseButton
              closeButtonLabel="Dismiss"
              onClose={() => setDeleteConflict(undefined)}
            >
              {DEPENDENT_KINDS.map(([kind, label]) => {
                const dependents = deleteConflict.dependents[kind];
                if (!dependents?.length) {
                  return null;
                }

                return (
                  <Box key={kind} mb="xs">
                    <Text size="xs" fw={600}>
                      {label}
                    </Text>
                    <List size="sm" withPadding>
                      {dependents.map((dependent) => (
                        <List.Item key={dependent.id}>
                          {dependent.name} (id {dependent.id})
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                );
              })}
              <Text size="xs">
                Detach the secret from these first, or revoke it instead if the credential has leaked.
              </Text>
            </Alert>
          )}

          {secrets.length === 0 ? (
            <Text c="dimmed" size="sm">
              No secrets yet. Create one to use dynamic tokens in your pipelines.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Details</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {secrets.map((secret) => (
                  <Table.Tr key={secret.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {secret.name}
                        </Text>
                        {secret.revokedAt && (
                          <Badge variant="light" color="red">
                            Revoked
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={secret.type === 'token_refresh' ? 'blue' : 'gray'}>
                        {secret.type === 'token_refresh' ? 'Token Refresh' : 'Static'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {secret.type === 'token_refresh' ? (
                        <Text size="xs" c="dimmed">
                          {secret.refreshUrl}
                          {secret.refreshResponseField && <> · field: {secret.refreshResponseField}</>}
                          {secret.tokenTtlSeconds && <> · TTL: {secret.tokenTtlSeconds}s</>}
                          {secret.accessTokenExpiresAt && (
                            <> · cached until {new Date(secret.accessTokenExpiresAt).toLocaleString()}</>
                          )}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          ••••••••
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(secret.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {/* Revoking is idempotent, so an already-revoked secret has no action left. */}
                        {!secret.revokedAt && (
                          <Tooltip label="Revoke — stops this secret resolving, permanently. Use this if it has leaked.">
                            <ActionIcon
                              color="orange"
                              variant="subtle"
                              aria-label={`Revoke ${secret.name}`}
                              onClick={() => setConfirmRevokeId(secret.id)}
                            >
                              <IconBan size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Delete — only possible while nothing references this secret.">
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            aria-label={`Delete ${secret.name}`}
                            onClick={() => setConfirmDeleteId(secret.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card.Section>
      </Card>

      <ConfirmationModal
        show={confirmDeleteId !== null}
        close={() => { setConfirmDeleteId(null); setDeleteError(undefined); }}
        confirm={handleDelete}
        loading={deleting}
        error={deleteError}
        message="This secret will be permanently deleted. Delete only succeeds while nothing references it — if a pipeline, webhook, or connector still uses it, the API will refuse and list what to detach first. For a credential that has leaked, use Revoke instead: it keeps the links intact but stops the secret resolving."
      />

      <ConfirmationModal
        show={confirmRevokeId !== null}
        close={() => { setConfirmRevokeId(null); setRevokeError(undefined); }}
        confirm={handleRevoke}
        loading={revoking}
        error={revokeError}
        message="This secret will stop resolving immediately and everything using it will start failing. Revoking is permanent and cannot be undone — a replacement has to be created as a new secret."
      />
    </>
  );
};

export default SettingsTabSecrets;
